import * as THREE from 'three';

import {
  CHURCH_CONFIG,
  LIGHTING_CONFIG,
  QUALITY_PRESETS,
} from '../config.js';

/** Configures the renderer and the two-light clear-afternoon rig. */
export class Lighting {
  constructor(scene, renderer, { quality = 'MEDIUM' } = {}) {
    this.scene = scene;
    this.renderer = renderer;
    this.config = LIGHTING_CONFIG;
    this.quality = QUALITY_PRESETS[quality] ?? QUALITY_PRESETS.MEDIUM;
    this.group = new THREE.Group();
    this.group.name = 'outdoor-lighting';

    this._configureScene();
    this._configureRenderer();
    this._createLights();
    scene.add(this.group);
  }

  _configureScene() {
    this.scene.background = new THREE.Color(this.config.background);
    this.scene.fog = new THREE.Fog(
      this.config.fog.color,
      this.config.fog.near,
      this.config.fog.far,
    );
  }

  _configureRenderer() {
    if (!this.renderer) return;

    this.renderer.outputColorSpace = THREE.SRGBColorSpace;
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
    this.renderer.toneMappingExposure = this.config.exposure;
    this.renderer.shadowMap.enabled = Boolean(this.quality.shadows);
    this.renderer.shadowMap.type = THREE.PCFSoftShadowMap;
  }

  _createLights() {
    const hemisphereConfig = this.config.hemisphere;
    this.hemisphere = new THREE.HemisphereLight(
      hemisphereConfig.skyColor,
      hemisphereConfig.groundColor,
      hemisphereConfig.intensity,
    );
    this.hemisphere.name = 'afternoon-sky-light';
    this.group.add(this.hemisphere);

    const sunConfig = this.config.sun;
    this.sun = new THREE.DirectionalLight(sunConfig.color, sunConfig.intensity);
    this.sun.name = 'afternoon-sun';
    this.sun.position.fromArray(sunConfig.position);
    this.sun.castShadow = Boolean(this.quality.shadows);

    this.sunTarget = new THREE.Object3D();
    this.sunTarget.name = 'sun-target';
    this.sunTarget.position.set(
      CHURCH_CONFIG.position[0],
      CHURCH_CONFIG.position[1],
      CHURCH_CONFIG.position[2],
    );
    this.sun.target = this.sunTarget;

    const shadowConfig = sunConfig.shadow;
    const shadowDistance = this.quality.shadowDistance ?? shadowConfig.cameraSize;
    const shadowMapSize = this.quality.shadowMapSize ?? shadowConfig.mapSize;
    this.sun.shadow.mapSize.set(shadowMapSize, shadowMapSize);
    this.sun.shadow.camera.left = -shadowDistance;
    this.sun.shadow.camera.right = shadowDistance;
    this.sun.shadow.camera.top = shadowDistance;
    this.sun.shadow.camera.bottom = -shadowDistance;
    this.sun.shadow.camera.near = shadowConfig.near;
    this.sun.shadow.camera.far = shadowConfig.far;
    this.sun.shadow.bias = shadowConfig.bias;
    this.sun.shadow.normalBias = shadowConfig.normalBias;
    this.sun.shadow.camera.updateProjectionMatrix();

    this.group.add(this.sun, this.sunTarget);
  }

  dispose() {
    this.group.removeFromParent();
    this.sun?.shadow.map?.dispose();
    this.scene.background = null;
    this.scene.fog = null;
  }
}

export default Lighting;
